import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useUser } from '@clerk/clerk-react';
import apiClient, { setClerkUserId, setMerchantId } from '@/lib/api-client';

interface MerchantProfile {
    id: number;
    clerk_user_id: string;
    business_name: string | null;
    email: string | null;
    shopify_shop_name: string | null;
    shopify_access_token: string | null;
    webhook_id: string | null;
    plan: string | null;
    email_subject: string | null;
    email_body: string | null;
    discount_min: number;
    discount_max: number;
    shipping_threshold: number | null;
    progress_bar_active: boolean;
    stripe_customer_id: string | null;
    subscription_id: string | null;
    subscription_status: string | null;
    role: string;
    teamRole: string; // 'owner' | 'member'
    stats: {
        products: number;
        orders: number;
        upsells: number;
    };
    shopify_connected: boolean;
}

interface MerchantContextType {
    merchant: MerchantProfile | null;
    loading: boolean;
    error: string | null;
    isOnboarded: boolean;
    isShopifyConnected: boolean;
    refreshMerchant: () => Promise<void>;
    connectShopify: (shopName: string, accessToken: string) => Promise<boolean>;
    syncProducts: () => Promise<{ count: number } | null>;
    disconnectShopify: () => Promise<boolean>;
    updateSettings: (settings: { 
        email_subject?: string; 
        email_body?: string; 
        discount_min?: number; 
        discount_max?: number;
        shipping_threshold?: number;
        progress_bar_active?: boolean;
    }) => Promise<boolean>;
    createCheckoutSession: () => Promise<void>;
    isAdmin: boolean;
    isOwner: boolean;
    isTeamMember: boolean;
    teamRole: string;
}

const MerchantContext = createContext<MerchantContextType>({
    merchant: null,
    loading: true,
    error: null,
    isOnboarded: false,
    isShopifyConnected: false,
    refreshMerchant: async () => { },
    connectShopify: async () => false,
    syncProducts: async () => null,
    disconnectShopify: async () => false,
    updateSettings: async () => false,
    createCheckoutSession: async () => { },
    isAdmin: false,
    isOwner: true,
    isTeamMember: false,
    teamRole: 'owner',
});

export function MerchantProvider({ children }: { children: React.ReactNode }) {
    const { user, isLoaded: isClerkLoaded } = useUser();
    const [merchant, setMerchant] = useState<MerchantProfile | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const hasInitialized = React.useRef(false);

    // Auto-register merchant when Clerk user is available
    const initMerchant = useCallback(async () => {
        if (!isClerkLoaded || !user || hasInitialized.current) {
            if (!isClerkLoaded || !user) setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);
            hasInitialized.current = true;

            // Set Clerk user ID for all API calls
            setClerkUserId(user.id);

            // Step 1: Register (or get existing) merchant
            // Try multiple email sources from Clerk for robustness
            const clerkEmail = user.primaryEmailAddress?.emailAddress 
                || user.emailAddresses?.[0]?.emailAddress 
                || null;

            const registerRes = await apiClient.post('/merchant/register', {
                clerk_user_id: user.id,
                business_name: user.fullName || user.firstName || 'My Store',
                email: clerkEmail,
            });

            // Enhanced register already returns full profile with stats
            const merchantData = registerRes.data.merchant;
            setMerchantId(merchantData.id);
            setMerchant({
                ...merchantData,
                teamRole: merchantData.teamRole || 'owner',
            });

            console.log(`[Merchant] Initialized: ${merchantData.business_name} (ID: ${merchantData.id}, teamRole: ${merchantData.teamRole || 'owner'})`);
        } catch (err: any) {
            console.error('[Merchant] Init failed:', err);
            setError(err.response?.data?.error || 'Failed to initialize merchant');
            // Reset initialized flag on error to allow retry
            hasInitialized.current = false;
        } finally {
            setLoading(false);
        }
    }, [isClerkLoaded, user]);

    useEffect(() => {
        initMerchant();
    }, [initMerchant]);

    const refreshMerchant = async () => {
        if (!merchant) return;
        try {
            const res = await apiClient.get('/merchant/profile');
            setMerchant({
                ...res.data,
                teamRole: res.data.teamRole || merchant.teamRole || 'owner',
            });
        } catch (err) {
            console.error('[Merchant] Refresh failed:', err);
        }
    };

    const connectShopify = async (shopName: string, accessToken: string): Promise<boolean> => {
        try {
            await apiClient.post('/merchant/connect-shopify', {
                shop_name: shopName,
                access_token: accessToken,
            });
            await refreshMerchant();
            return true;
        } catch (err: any) {
            console.error('[Merchant] Connect Shopify failed:', err);
            throw new Error(err.response?.data?.error || 'Failed to connect Shopify');
        }
    };

    const syncProducts = async (): Promise<{ count: number } | null> => {
        try {
            const res = await apiClient.post('/merchant/sync-products');
            await refreshMerchant();
            return { count: res.data.count };
        } catch (err: any) {
            console.error('[Merchant] Sync failed:', err);
            throw new Error(err.response?.data?.error || 'Failed to sync products');
        }
    };

    const disconnectShopify = async (): Promise<boolean> => {
        try {
            await apiClient.post('/merchant/disconnect-shopify');
            await refreshMerchant();
            return true;
        } catch (err) {
            console.error('[Merchant] Disconnect failed:', err);
            return false;
        }
    };

    const updateSettings = async (settings: { 
        email_subject?: string; 
        email_body?: string;
        discount_min?: number;
        discount_max?: number;
        shipping_threshold?: number;
        progress_bar_active?: boolean;
    }): Promise<boolean> => {
        try {
            await apiClient.put('/merchant/settings', settings);
            await refreshMerchant();
            return true;
        } catch (err: any) {
            console.error('[Merchant] Update settings failed:', err);
            throw new Error(err.response?.data?.error || 'Failed to update settings');
        }
    };

    const createCheckoutSession = async () => {
        try {
            const res = await apiClient.post('/payments/create-checkout-session', {
                successUrl: window.location.origin + '/dashboard?payment=success',
                cancelUrl: window.location.origin + '/dashboard?payment=cancel',
            });
            
            if (res.data.url) {
                window.location.href = res.data.url;
            }
        } catch (err: any) {
            console.error('[Merchant] Checkout failed:', err);
            throw new Error(err.response?.data?.error || 'Failed to start checkout');
        }
    };

    const teamRole = merchant?.teamRole || 'owner';
    const isOwner = teamRole === 'owner';
    const isTeamMember = teamRole === 'member';

    return (
        <MerchantContext.Provider
            value={{
                merchant,
                loading,
                error,
                isOnboarded: !!merchant,
                isShopifyConnected: !!merchant?.shopify_connected,
                refreshMerchant,
                connectShopify,
                syncProducts,
                disconnectShopify,
                updateSettings,
                createCheckoutSession,
                isAdmin: merchant?.role === 'admin',
                isOwner,
                isTeamMember,
                teamRole,
            }}
        >
            {children}
        </MerchantContext.Provider>
    );
}

export const useMerchant = () => useContext(MerchantContext);
